import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  Placement
} from '@floating-ui/react';

interface TooltipProps {
  content: string | React.ReactNode;
  children: React.ReactNode;
  position?: Placement;
  delay?: number;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 0.2,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const arrowRef = React.useRef(null);

  const {
    x,
    y,
    strategy,
    refs,
    context
  } = useFloating({
    placement: position,
    middleware: [
      offset(8),
      flip(),
      shift(),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  let timeout: NodeJS.Timeout;

  const handleMouseEnter = () => {
    timeout = setTimeout(() => {
      setIsVisible(true);
    }, delay * 1000);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeout);
    setIsVisible(false);
  };

  const variants = {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 350,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.1
      }
    }
  };

  return (
    <div
      ref={refs.setReference}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="inline-block relative"
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={refs.setFloating}
            className={`absolute z-50 px-3.5 py-2.5 text-xs font-medium bg-[#F6F6F6] dark:bg-[#141421] text-content-primary dark:text-white rounded-md shadow-lg ${className}`}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content',
              maxWidth: '250px',
              whiteSpace: 'normal'
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
          >
            {content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className="fill-[#F6F6F6] dark:fill-[#141421]"
              tipRadius={1}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
